import React from 'react';
import { Modal, Box, Typography, Button } from "@mui/material";
import '../../Styles/AssetVarianceTable.css';

const AutoRebalanceConfirmationModal = ({
  open,
  onClose,
  rebalanceData,
  onConfirm,
  strategyName,
  orders = [],
  currentStep = 1,
  onStepBack
}) => {
  // Extract investor name from rebalanceData
  const investorName = React.useMemo(() => {
    if (!Array.isArray(rebalanceData) || rebalanceData.length === 0) {
      return "Investor";
    }
    // Get the first investor name from the data
    const firstRow = rebalanceData[0];
    return firstRow?.investor || "Investor";
  }, [rebalanceData]);


  // Group rows by investor to ensure rowSpan applies only to contiguous rows
  const groupedRows = React.useMemo(() => {
    if (!Array.isArray(rebalanceData)) return [];
    const investorToRowsMap = new Map();
    rebalanceData.forEach((row, index) => {
      const investorKey = row?.uniqueInvestorId || row?.investor || '';
      if (!investorToRowsMap.has(investorKey)) {
        investorToRowsMap.set(investorKey, []);
      }
      investorToRowsMap.get(investorKey).push({ ...row, originalIndex: index });
    });

    const result = [];
    investorToRowsMap.forEach((rows, investorKey) => {
      rows.forEach((row, idx) => {
        result.push({
          ...row,
          __isFirstInvestorRow: idx === 0,
          __investorRowSpan: rows.length,
        });
      });
    });
    return result;
  }, [rebalanceData]);

  const tableCellStyles = {
    padding: "12px 24px",
    textAlign: "center",
    boxShadow: "inset 0 -1px 0 #e0e0e0",
    overflow: 'hidden',
    boxSizing: 'border-box',
    position: 'relative',
    backgroundColor: '#fff',
  };
  const tableStyles = {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    tableLayout: 'fixed',
  };

  // Determine which price column to show based on order type (Market vs Limit)
  const isLimitOrder = Array.isArray(orders) && orders.length > 0 && orders.some(o => (o.orderType || '').toUpperCase() === 'LMT');
  const showMarketPrice = !isLimitOrder;
  const showLimitPrice = isLimitOrder;
  const baseColumnCount = 7; // Investor, Asset, Share Count, Sell Quantity, Days Held, Cost Price, Total Profit (Loss)
  const totalColumns = baseColumnCount + 1; // +1 for either Market or Limit Price

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="confirmation-modal-container">
        <Box className='confirmation-modal-header-container'>
        <Box className="confirmation-modal-header">
            {/* <Typography variant="h5" className="confirmation-modal-title">
              Lots closing for: <span className="confirmation-modal-strategy-name">{strategyName}</span>
            </Typography> */}

            <Typography variant="h6" style={{ fontSize: '22px', fontWeight: 500 }}>
              Lots closing for: <span className="confirmation-modal-strategy-name">{strategyName}</span>
            </Typography>
          
          <Button
            onClick={onStepBack || onClose}
            sx={{
              padding: "8px",
              minWidth: "auto",
              color: "#666",
              fontSize: "24px",
              fontWeight: "600",
              lineHeight: "32px",
              fontFamily: "Roboto",
              fontStyle: "normal",
              textAlign: "center",
              '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#333',
              }
          }}
          >
            ×
          </Button>
        </Box>

        <Box className='confirmation-modal-header-content'>
            <Typography className="confirmation-modal-subtitle">
              Preview your lots closing for {investorName}
            </Typography>
            <Typography className="confirmation-modal-description">
              Preview all trades under your order:
            </Typography>
          </Box>
        </Box>
        

        <Box className="confirmation-table-container">
                     <table style={tableStyles}>
            <colgroup>
              <col style={{ width: '10%' }} /> {/* Investor */}
              <col style={{ width: '8%' }} />  {/* Asset */}
              <col style={{ width: '10%' }} /> {/* Share Count */}
              <col style={{ width: '10%' }} /> {/* Sell Quantity */}
              <col style={{ width: '10%' }} /> {/* Days Held */}
              <col style={{ width: '10%' }} /> {/* Cost Price */}
              {showMarketPrice && <col style={{ width: '10%' }} />} {/* Market Price */}
              {showLimitPrice && <col style={{ width: '10%' }} />} {/* Limit Price */}
              <col style={{ width: '14%' }} /> {/* Total Profit (Loss) */}

            </colgroup>
            <thead>
              <tr>
                <th className="confirmation-table-header">Investor</th>
                <th className="confirmation-table-header">Asset</th>
                <th className="confirmation-table-header">Share Count</th>
                <th className="confirmation-table-header">Sell Quantity</th>
                <th className="confirmation-table-header">Days Held</th>
                <th className="confirmation-table-header">Cost Price</th>
                {showMarketPrice && (
                  <th className="confirmation-table-header">Market Price</th>
                )}
                {showLimitPrice && (
                  <th className="confirmation-table-header">Limit Price</th>
                )}
                <th className="confirmation-table-header">Total Profit (Loss)</th>
              </tr>
            </thead>
            <tbody>
              {groupedRows.length > 0 ? (
                groupedRows.map((row, index) => {
                  const isFirstInvestorRow = row.__isFirstInvestorRow;
                  const investorRowSpan = row.__investorRowSpan;
                  return (
                    <tr key={index} className="confirmation-table-row">
                      {isFirstInvestorRow && (
                        <td
                          rowSpan={investorRowSpan}
                          className="confirmation-table-cell confirmation-table-cell-investor"
                          style={{ ...tableCellStyles, textAlign: 'left', fontWeight: 400, boxShadow: '2px 0 4px rgba(0,0,0,0.1), inset 0 -1px 0 #e0e0e0' }}
                        >
                          {row.investor}
                        </td>
                      )}
                      <td className="confirmation-table-cell" style={tableCellStyles}>
                        {row.symbol}
                      </td>
                      <td className="confirmation-table-cell" style={tableCellStyles}>
                        {!isNaN(Number(row.shareCount)) && row.shareCount !== null && row.shareCount !== undefined
                          ? `$${Number(row.shareCount).toFixed(0)}`
                          : '-'}
                      </td>
                      <td className="confirmation-table-cell" style={tableCellStyles}>
                        {!isNaN(Number(row.sellQuantity)) && row.sellQuantity !== null && row.sellQuantity !== undefined
                          ? Number(row.sellQuantity).toFixed(1)
                          : '-'}
                      </td>
                      <td className="confirmation-table-cell" style={tableCellStyles}>
                        {row.daysHeld}
                      </td>
                      <td className="confirmation-table-cell" style={tableCellStyles}>
                        {!isNaN(Number(row.costPrice)) && row.costPrice !== null && row.costPrice !== undefined
                          ? `$${Number(row.costPrice).toFixed(2)}`
                          : '-'}
                      </td>
                      {showMarketPrice && (
                        <td className="confirmation-table-cell" style={tableCellStyles}>
                          {!isNaN(Number(row.marketPrice)) && row.marketPrice !== null && row.marketPrice !== undefined
                            ? `$${Number(row.marketPrice).toFixed(2)}`
                            : '-'}
                        </td>
                      )}
                      {showLimitPrice && (
                        <td className="confirmation-table-cell" style={tableCellStyles}>
                          {(() => {
                            const matchedOrder = Array.isArray(orders) ? orders.find(o => o.asset === row.symbol) : null;
                            const limitPrice = matchedOrder && matchedOrder.limitPrice !== undefined && matchedOrder.limitPrice !== null && matchedOrder.limitPrice !== ''
                              ? Number(matchedOrder.limitPrice)
                              : ( !isNaN(Number(row.marketPrice)) ? Number(row.marketPrice) : NaN );
                            return !isNaN(limitPrice) ? `$${limitPrice.toFixed(2)}` : '-';
                          })()}
                        </td>
                      )}
                      <td className={`confirmation-table-cell ${parseFloat(row.totalProfit) >= 0 ? 'confirmation-table-cell-profit' : 'confirmation-table-cell-loss'}`} style={tableCellStyles}>
                        ${Math.abs(row.totalProfit).toFixed(2)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan={totalColumns} style={{ textAlign: 'center', padding: 20, fontSize: '14px' }}>No data available</td></tr>
              )}
            </tbody>
          </table>
        </Box>

        <Box className="confirmation-modal-footer">
          <Button
            onClick={onStepBack || onClose}
            variant="outlined"
            className="confirmation-modal-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            className="confirmation-modal-continue-button"
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AutoRebalanceConfirmationModal;
